import { Heart, Repeat, Building, Package, Check, Mail, Coffee } from "lucide-react";
import { IMAGES, SITE } from "@/config/site";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { Photo } from "@/components/site/Photo";
import { Reveal, SectionHeading, DonateButton, CtaLink } from "@/components/site/Primitives";
import { FormBuilder } from "@/components/forms/FormBuilder";
import { FloridaDisclosure } from "@/components/site/FloridaDisclosure";
import { SPONSOR_FIELDS, VOLUNTEER_FIELDS } from "@/components/forms/formDefs";

const FUNDS = [
  { id: "service-dog", icon: Heart, eyebrow: "Service Dog Program", t: "Donate to Change Two Lives", d: "Help connect approved first responders and veterans with carefully evaluated, professionally trained dogs through our partnership with Big Dog Ranch Rescue.", href: SITE.donateLinks.serviceDog, qr: "/images/donate-qr.svg", variant: "btn-red" },
  { id: "canteen", icon: Coffee, eyebrow: "Canteen 33", t: "Canteen & Rehab", d: "Keep Canteen 33 stocked with water, electrolyte beverages, coffee, and food for responders working extended incidents.", href: SITE.donateLinks.canteen, qr: "/images/donate-qr-canteen.svg", variant: "btn-gold" },
  { id: "general", icon: Building, eyebrow: "General Foundation Support", t: "Donate to Make a Difference", d: "Support the Foundation's mission wherever the need is greatest, across all of our programs.", href: SITE.donateLinks.general, qr: "/images/donate-qr-general.svg", variant: "btn-navy" },
];

const OPTIONS = [
  { icon: Repeat, t: "Monthly Giving", d: "Provide steady, reliable support that helps programs plan ahead.", action: <DonateButton label="Give Monthly" monthly variant="btn-gold" testId="give-monthly-button" /> },
  { icon: Building, t: "Corporate Giving", d: "Partner with the Foundation through sponsorship or workplace giving.", action: <CtaLink to="#sponsor" variant="btn-navy" testId="give-corporate-button">Become a Sponsor</CtaLink> },
  { icon: Package, t: "In-Kind Donations", d: "Contribute approved supplies, equipment, or professional services.", action: <CtaLink to="#in-kind" variant="btn-ghost" testId="give-in-kind-button">Learn More</CtaLink> },
];

const LEVELS = [
  { t: "Community Supporter", v: "$250" },
  { t: "Bronze Sponsor", v: "$500" },
  { t: "Silver Sponsor", v: "$1,000" },
  { t: "Gold Sponsor", v: "$2,500" },
  { t: "Corporate Partner", v: "Custom contribution" },
];

const FundCard = ({ f, i }) => (
  <Reveal delay={i * 0.08} className="flex flex-col border border-navy/10 bg-white p-8 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-gold" data-testid={`donate-fund-${f.id}`}>
    <f.icon className="text-navy" size={28} />
    <p className="mt-8 text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-deep">{f.eyebrow}</p>
    <h3 className="mt-2 text-2xl font-extrabold">{f.t}</h3>
    <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/75">{f.d}</p>
    <DonateButton label="Donate" href={f.href} variant={f.variant} className="mt-8 w-full" testId={`donate-fund-${f.id}-button`} />
    <div className="mt-6 flex items-center gap-4 border-t border-navy/10 pt-6">
      <img src={f.qr} alt={`QR code for the ${f.t} donation page on Zeffy`} className="h-20 w-20" width="80" height="80" loading="lazy" data-testid={`donate-fund-${f.id}-qr`} />
      <p className="text-xs leading-relaxed text-slate">Scan with your phone's camera to open this Zeffy donation page.</p>
    </div>
  </Reveal>
);

const PARTNERS = ["Local businesses", "Corporate sponsors", "Fire service industry partners", "Law enforcement supporters", "Veterinary and dog training partners", "Community organizations"];

const Options = () => (
  <section id="donate-options" className="py-24 md:py-32" data-testid="give-options-section">
    <div className="container-x">
      <SectionHeading eyebrow="Ways to Give" title="Choose Where Your Gift Goes." intro={`Donations are processed securely through Zeffy, the Foundation's online giving platform. ${SITE.name} does not collect or store payment card information on this website.`} />
      <div className="mt-14 grid gap-5 lg:grid-cols-3">
        {FUNDS.map((f, i) => <FundCard key={f.id} f={f} i={i} />)}
      </div>
      <div className="mt-5 grid gap-5 md:grid-cols-3">
        {OPTIONS.map((o, i) => (
          <Reveal key={o.t} delay={i * 0.07} className="flex flex-col border border-navy/10 bg-white p-8 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-gold" data-testid={`give-option-${i + 1}`}>
            <o.icon className="text-navy" size={28} />
            <h3 className="mt-8 text-xl font-bold">{o.t}</h3>
            <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/75">{o.d}</p>
            <div className="mt-8 [&_.btn]:w-full">{o.action}</div>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-10 grid gap-8 border-l-2 border-gold bg-white p-6 md:grid-cols-[1.3fr_1fr] md:p-10" data-testid="donor-thank-you-note">
        <div>
          <p className="eyebrow">A Note of Thanks</p>
          <h3 className="mt-4 text-2xl font-extrabold md:text-3xl">Thank you for standing with those who serve.</h3>
          <p className="mt-4 leading-relaxed text-ink/80">Every gift, whatever its size, helps us care for the people who care for our communities, and the dogs who walk beside them. We are grateful for your trust and your generosity.</p>
        </div>
        <div className="border-t border-navy/10 pt-6 text-sm leading-relaxed text-ink/80 md:border-l md:border-t-0 md:pl-8 md:pt-0" data-testid="donor-tax-info">
          <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-navy">For your tax records</p>
          <p className="mt-3">{SITE.name} is {SITE.nonprofit.status}.</p>
          <p className="mt-2">EIN: <span className="font-semibold text-navy">{SITE.nonprofit.ein}</span></p>
          <p className="mt-2">{SITE.nonprofit.taxDeductibility} Please keep the confirmation you receive from Zeffy for your records.</p>
        </div>
      </Reveal>
      <Reveal className="mt-10 grid gap-6 border border-navy/10 bg-white p-6 md:grid-cols-[auto_1fr_1fr] md:items-start md:gap-10 md:p-8" data-testid="give-by-check-card">
        <Mail className="text-navy" size={28} />
        <div>
          <h3 className="text-lg font-bold">Give by Check</h3>
          <p className="mt-2 text-sm leading-relaxed text-ink/75">Prefer to give by mail? Make your check payable to <span className="font-semibold text-navy">{SITE.contact.checkPayableTo}</span> and include your email address if you would like an acknowledgment of your gift.</p>
        </div>
        <address className="not-italic text-sm leading-relaxed text-ink" data-testid="give-by-check-address">
          <span className="font-display text-xs font-bold uppercase tracking-[0.2em] text-navy">Mail to</span>
          <span className="mt-2 block font-semibold">{SITE.contact.checkPayableTo}</span>
          <span className="block">{SITE.contact.mailingAddress}</span>
        </address>
      </Reveal>
      <div className="mt-8 border border-navy/10 bg-mist p-6 md:p-8"><FloridaDisclosure testId="give-florida-disclosure" /></div>
    </div>
  </section>
);

const Monthly = () => (
  <section id="monthly" className="grain relative overflow-hidden bg-navy py-24 text-paper md:py-32" data-testid="give-monthly-section">
    <div className="container-x grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
      <div>
        <SectionHeading light eyebrow="Monthly Giving" title="Become a Monthly Mission Supporter." intro="Recurring gifts give the Foundation something every responsible program needs: predictability. Monthly support helps sustain dog evaluations and training, keep Canteen 33 stocked and ready, and plan responsibly for the months ahead." />
        <ul className="mt-10 space-y-4">
          {["Sustains programs between fundraising events", "Helps the Foundation plan training and deployments responsibly", "Easy to start, adjust, or cancel through Zeffy"].map((t) => (
            <li key={t} className="flex items-start gap-3 text-[#D5DEE7]"><Check className="mt-0.5 shrink-0 text-gold" size={18} />{t}</li>
          ))}
        </ul>
        <DonateButton label="Start Monthly Giving" monthly variant="btn-gold" className="mt-10" testId="monthly-donate-button" />
      </div>
      <Photo image={IMAGES.luna} ratio="aspect-[4/5]" className="mx-auto w-full max-w-md" />
    </div>
  </section>
);

const Sponsor = () => (
  <section id="sponsor" className="py-24 md:py-32" data-testid="give-sponsor-section">
    <div className="container-x">
      <SectionHeading eyebrow="Corporate Sponsorship" title="Partner With Purpose." intro="We welcome local businesses, corporate sponsors, fire service industry partners, law enforcement supporters, veterinary and dog training partners, and community organizations who share our commitment to those who serve." />
      <div className="mt-14 grid gap-px border border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-5" data-testid="sponsorship-levels">
        {LEVELS.map((l, i) => (
          <Reveal key={l.t} delay={i * 0.06} className="bg-white p-7" data-testid={`sponsor-level-${i + 1}`}>
            <p className="font-display text-xs font-bold text-gold-deep">0{i + 1}</p>
            <h3 className="mt-6 text-lg font-bold">{l.t}</h3>
            <p className="mt-2 font-display text-2xl font-black text-navy">{l.v}</p>
          </Reveal>
        ))}
      </div>
      <p className="mt-5 text-sm text-slate" data-testid="sponsorship-levels-note">Proposed levels. Please contact the Foundation before making sponsorship arrangements.</p>
    </div>
    <div className="container-x mt-16 grid gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
      <div>
        <h3 className="text-2xl font-extrabold md:text-3xl">Start the Conversation.</h3>
        <p className="mt-4 leading-relaxed text-ink/80">Tell us about your organization, the level you are considering, and the program you would most like to support. A member of the Foundation will follow up to discuss next steps.</p>
        <ul className="mt-10 border-t border-navy/15">
          {PARTNERS.map((p) => (
            <li key={p} className="border-b border-navy/15 py-4 font-display font-bold text-navy">{p}</li>
          ))}
        </ul>
      </div>
      <Reveal delay={0.1} className="border border-navy/10 bg-white p-6 shadow-[0_12px_40px_rgba(16,38,63,0.08)] md:p-10">
        <h3 className="mb-8 text-2xl font-bold">Sponsorship Inquiry</h3>
        <FormBuilder formType="sponsor" fields={SPONSOR_FIELDS} testId="sponsor-form" submitLabel="Send Inquiry" successTitle="Thank you for your interest in partnering." successText="A member of the Foundation team will contact you to discuss sponsorship opportunities." />
      </Reveal>
    </div>
  </section>
);

const Volunteer = () => (
  <section id="volunteer" className="bg-mist py-24 md:py-32" data-testid="give-volunteer-section">
    <div className="container-x grid gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
      <div>
        <SectionHeading eyebrow="Volunteer" title="Lend a Hand to Those Who Serve." intro="Volunteers help staff Canteen 33, support events, and assist with the day-to-day work that keeps programs running. Tell us where you would like to help, and we will be in touch about current opportunities." />
        <Reveal delay={0.1} className="mt-10 border-l-2 border-gold bg-white p-6 text-sm leading-relaxed text-ink/80" data-testid="volunteer-requirements">
          <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-navy">Canteen 33 volunteers</p>
          <p className="mt-3">Approved volunteers must complete Foundation orientation and required NIMS/ICS training, including ICS-100, ICS-200, ICS-300, and IS-700, as established by Foundation policy.</p>
        </Reveal>
      </div>
      <Reveal delay={0.1} className="border border-navy/10 bg-white p-6 shadow-[0_12px_40px_rgba(16,38,63,0.08)] md:p-10">
        <h3 className="mb-8 text-2xl font-bold">Volunteer Interest Form</h3>
        <FormBuilder formType="volunteer" fields={VOLUNTEER_FIELDS} testId="volunteer-form" submitLabel="Submit Interest" successTitle="Thank you for volunteering your time." successText="We have received your volunteer interest form and will contact you when an opportunity matches your interests and availability." footnote="Canteen 33 roles require Foundation orientation and NIMS/ICS training. Some roles may also require a background screening." />
      </Reveal>
    </div>
  </section>
);

const InKind = () => (
  <section id="in-kind" className="py-24 md:py-32" data-testid="give-in-kind-section">
    <div className="container-x grid items-center gap-10 lg:grid-cols-[1.2fr_0.8fr]">
      <SectionHeading eyebrow="In-Kind Contributions" title="Supplies, Equipment, and Services." intro="The Foundation may accept approved donations of supplies, equipment, services, and other resources that directly support its programs, such as items for Canteen 33 or materials used in dog care and training. To ensure every contribution can be used responsibly, please contact us before dropping off or shipping any items." />
      <Reveal delay={0.1}><CtaLink to="/about#contact" testId="in-kind-contact-button">Contact Us About In-Kind Giving</CtaLink></Reveal>
    </div>
  </section>
);

export default function GetInvolved() {
  return (
    <>
      <Seo title="Get Involved | Donate, Sponsor, or Volunteer for First Responders" description="Give to the Service Dog Program, Canteen 33, or general Foundation support. Sponsor, volunteer, or give in-kind to support first responders in Palm Beach County." path="/get-involved" />
      <div id="donate" />
      <PageHero eyebrow="Get Involved" lines={[{ text: "Make a Difference" }, { text: "for Those", className: "text-gold" }, { text: "Who Serve." }]} intro="Every gift, partnership, and volunteer hour helps the Foundation stand beside firefighters, law enforcement officers, emergency medical personnel, dispatchers, and veterans." image={IMAGES.lunaResting}>
        <div className="mt-10 flex flex-col gap-3 sm:flex-row">
          <DonateButton label="Donate Now" href="#donate-options" testId="give-hero-donate-button" />
          <CtaLink to="#volunteer" variant="btn-ghost-light" testId="give-hero-volunteer-button">Volunteer</CtaLink>
        </div>
      </PageHero>
      <Options />
      <Monthly />
      <Sponsor />
      <Volunteer />
      <InKind />
    </>
  );
}
